import axios from 'axios';
import { IS_NATIVE } from '@/lib/nativeContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Native push registration (Android/iOS via FCM), the counterpart to
 * usePushNotifications.js's web/VAPID flow. Separate module rather than
 * folded into that hook: the two run through entirely different browser
 * vs. native-plugin APIs, and usePushNotifications' own isSupported check
 * (PushManager in window) already correctly evaluates false inside the
 * native shell's WebView, so the two paths never race or double-register.
 *
 * No in-app toggle -- native apps request permission via the OS system
 * prompt once, near launch, same as any other native app. Users retain
 * full control afterward through the OS's own notification settings.
 */
let _attempted = false;
let _token = null;

async function saveToken(value) {
  _token = value;
  try {
    await axios.post(`${API}/push/subscribe`, {
      type: 'fcm',
      subscription: { token: value },
      // This build keeps the icon badge in step itself (nativeBadge.js),
      // so the backend may put the unread count on our pushes.
      badges: true,
    });
  } catch (err) {
    console.error('Native push subscribe failed:', err);
  }
}

export async function registerNativePush(userId) {
  if (!IS_NATIVE || !userId) return;
  // Signed in again in the same run of the app (after a log-out, which removed this phone's registration): hand the
  // same phone to this sign-in rather than waiting for the next launch (P0212).
  if (_attempted) {
    if (_token) await saveToken(_token);
    return;
  }
  _attempted = true;

  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');

    let status = (await PushNotifications.checkPermissions()).receive;
    if (status === 'prompt' || status === 'prompt-with-rationale') {
      status = (await PushNotifications.requestPermissions()).receive;
    }
    if (status !== 'granted') return;

    // The iPhone app hands over its token at every start (P0218), and on a first start iOS can hand the same token
    // over twice: sign it up once.
    PushNotifications.addListener('registration', (token) => {
      if (token.value !== _token) saveToken(token.value);
    });
    PushNotifications.addListener('registrationError', (err) => {
      console.error('Native push registration error:', err);
    });

    await PushNotifications.register();
  } catch (err) {
    console.error('Native push init failed:', err);
  }
}
